import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { OnboardingStep } from '../types/onboardingTypes'
import HeightInput, { HeightInputRef } from './HeightInput'
import StepSubtitle from './StepSubtitle.component'
import StepTitle from './StepTitle.component'
import { useFeatureValue } from '@growthbook/growthbook-react'
import { mapPrivacyPolicyLink } from 'Lib/LinkUtils'
import { getUserLanguage } from 'Lib/Localization'
import { FaExclamationCircle } from 'react-icons/fa'
import { theme } from 'styles/theme'

const CenteredContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Switch = styled.div`
  margin: auto;
  position: relative;
  width: 100px;
  height: ${({ theme }) => theme.spacing.large};
  background: #d9d9d9;
  border-radius: ${({ theme }) => theme.spacing.xsmall};
  padding: 2px;
  transition: 300ms all;

  &:before {
    transition: 300ms all;
    content: "";
    position: absolute;
    width: 50px;
    height: ${({ theme }) => theme.spacing.large};
    border-radius: ${({ theme }) => theme.spacing.xsmall};
    top: 50%;
    left: 2px;
    background: ${({ theme }) => theme.colors.primary};
    transform: translate(0, -50%);
  }
`

const SwitchInput = styled.input`
  display: none;

  &:checked + ${Switch} {
    &:before {
      transform: translate(100%, -50%);
    }
  }
`

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  margin: auto;
`

const Unit = styled.span< { selected: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  color: ${({ selected, theme }) => (selected ? theme.colors.white : theme.colors.text)};
`

const InputLabel = styled.span`
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 100%;
  position: relative;
  margin-top: 0px;
  margin-bottom: 0px;
`

const Error = styled.div`
  text-align: center;
  color: red;
  font-size: ${({ theme }) => theme.fontSizes.small};
  margin-top: ${({ theme }) => theme.spacing.xsmall};
  margin-bottom: 0px;
`

const CheckboxIcon = styled.svg<{ selected: boolean, warning: boolean }>`
  fill: ${({ selected, theme }) => (selected ? theme.colors.white : theme.colors.transparent)};
  stroke: ${({ selected, warning, theme }) => {
    if (selected) {
      return theme.colors.primary
    }

    if (warning) {
      return theme.colors.red400
    }

    return '#999'
  }};
  stroke-width: 1.5px;
  margin-right: ${({ theme }) => theme.spacing.small};
  margin-top: -3px;
`

const CheckboxWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-right: ${({ theme }) => theme.spacing.medium};
  padding-left: ${({ theme }) => theme.spacing.small};
  margin-top: ${({ theme }) => theme.spacing.large};
  margin-bottom: ${({ theme }) => theme.spacing.large};
  cursor: pointer;
  box-sizing: border-box;
`

const CheckboxLabel = styled.span`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-top: 2px;
  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    font-size: ${({ theme }) => theme.fontSizes.medium};
  }
`

const ConsentWarningContainer = styled.div`
  margin-bottom: ${props => props.theme.spacing.small};
  padding: ${props => props.theme.spacing.small};
  background-color: ${props => props.theme.colors.red100};
  border-radius: ${props => props.theme.spacing.small};
  margin-left: ${props => props.theme.spacing.small};
  margin-right: ${props => props.theme.spacing.small};
`

const ConsentWarningText = styled.span`
  font-size: ${props => props.theme.fontSizes.medium};
  color: ${props => props.theme.colors.red500};
  display: flex;
  gap: ${props => props.theme.spacing.small};
`

interface InputStepProps {
  stepConfig: OnboardingStep
  onHeightChange: (height: IUserHeight | undefined) => void
  initialUnit?: IUnitSystem
  initialHeight?: IUserHeight
}

const HeightInputStep: React.FC<InputStepProps> = ({ stepConfig, onHeightChange, initialUnit, initialHeight }) => {
  const [selectedUnit, setSelectedUnit] = useState<IUnitSystem>(initialUnit ?? 'cm/kg')
  const [isConsentSelected, setIsConsentSelected] = useState(false)
  const [isConsentWarning, setIsConsentWarning] = useState(false)
  const [height, setHeight] = useState<IUserHeight | undefined>(initialHeight)
  const [errorMessage, setErrorMessage] = useState('')

  const heightInputRef = useRef<HeightInputRef | null>(null)

  const { t } = useTranslation()
  const lang = getUserLanguage()

  const healthDataConsentCheckbox = useFeatureValue('use-health-data-consent-checkbox', false)

  const privacyUrl = mapPrivacyPolicyLink(lang)

  useEffect(() => {
    if (height && (healthDataConsentCheckbox && !isConsentSelected)) {
      setIsConsentWarning(true)
      onHeightChange(undefined)
    } else {
      setIsConsentWarning(false)
      onHeightChange(height)
    }
  }, [isConsentSelected, height])

  const handleUnitChange = (e: ChangeEvent<HTMLInputElement>) => {
    heightInputRef.current?.clearInputs()
    setErrorMessage('')
    setSelectedUnit(e.target.checked ? 'ft/lbs' : 'cm/kg')
  }

  return (
    <CenteredContainer>
      <StepTitle>{stepConfig.wordings!.title}</StepTitle>
      {!!stepConfig.wordings!.subtitle && <StepSubtitle>{stepConfig.wordings!.subtitle}</StepSubtitle>}
      <Label>
        <SwitchInput
          checked={selectedUnit === 'ft/lbs'}
          type="checkbox"
          onChange={handleUnitChange}
        />
        <Switch >
          <InputLabel>
            <Unit selected={selectedUnit === 'cm/kg'}>{t('height.cm')}</Unit>
            <Unit selected={selectedUnit === 'ft/lbs'}>{t('height.ft')}</Unit>
          </InputLabel>
        </Switch>
      </Label>
      <HeightInput
        placeholder={stepConfig.wordings!.placeholder!}
        unit={selectedUnit}
        initialHeight={initialHeight}
        onHeightChange={setHeight}
        setErrorMessage={setErrorMessage}
        ref={heightInputRef}
      />
      {errorMessage && <Error>{errorMessage}</Error>}
      {healthDataConsentCheckbox && (
        <div>
          <CheckboxWrapper onClick={() => setIsConsentSelected(!isConsentSelected)}>
            <CheckboxIcon
              width="28"
              height="28"
              viewBox="0 0 22 22"
              selected={isConsentSelected}
              warning={isConsentWarning}
            >
              <rect x="5" y="5" width="14" height="14" rx="4" ry="4" />
              {isConsentSelected && <path d="M9 12l2 2l4 -4" />}
            </CheckboxIcon>
            <CheckboxLabel>
              <span>
                <Trans
                  i18nKey={'healthDataConsentCheckbox.label'}
                  components={[<a href={privacyUrl} target='_self' />]}
                />
              </span>
            </CheckboxLabel>
          </CheckboxWrapper>
          {isConsentWarning && (
            <ConsentWarningContainer>
              <ConsentWarningText>
                <FaExclamationCircle size={20} color={theme.colors.red500} />
                <span>
                  {t('healthDataConsentCheckbox.warning')}
                </span>
              </ConsentWarningText>
            </ConsentWarningContainer>
          )}
        </div>
      )}
    </CenteredContainer>
  )
}

export default HeightInputStep
